import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import { Box, Button, Stack } from '@mui/material';
import {
  CheckCircleOutlineRounded,
  CancelOutlined,
  QueryBuilderOutlined,
} from '@mui/icons-material';

import { inboxMobileView, status } from 'venuescanner-js/constants/thread';
import { OrganiserEnquiryInfo } from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryInfo/OrganiserEnquiryInfo';
import OrganiserMenu from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryMenu/EnquiryMenu';
import CallButton from 'venuescanner-js/react/components/Inbox/components/Objects/CallButton';
import CtaMarkAsBooked from 'venuescanner-js/react/components/Inbox/components/Objects/Ctas/CtaMarkAsBooked';
import CtaNavBack from 'venuescanner-js/react/components/Inbox/components/Objects/Ctas/CtaNavBack';
import CtaShowToOrganiser from 'venuescanner-js/react/components/Inbox/components/Objects/Ctas/CtaShowToOrganiser';
import { ViewContext } from 'venuescanner-js/react/components/Inbox/context/ViewContext';
import EditButton from 'venuescanner-js/react/components/Inbox/components/Objects/EditButton';
import Tip from 'venuescanner-js/react/components/Inbox/components/Objects/Tip';

import EnquiryCtas from '../EnquiryCtas';
import './EnquiryDetails.scss';

dayjs.extend(isSameOrBefore);

const OrganiserEnquiryDetails = ({ can, send, state }) => {
  const { isMobileDevice, setActiveMobileView, activeUser } =
    useContext(ViewContext);
  const [hasCtas, setHasCtas] = useState(null);
  const { space, thread, venue } = state.context;
  const history = useHistory();

  const { name: spaceName, url: spaceUrl } = space.data();

  useEffect(() => {
    if (can !== undefined) {
      setHasCtas(
        !!(
          can?.EDIT ||
          can?.ACCEPT ||
          can?.REACCEPT ||
          can?.BOOK ||
          can?.REOPEN ||
          can?.DECLINE ||
          can?.CANCEL ||
          can?.DECLINE_OR_CANCEL ||
          can?.SEND_PAYMENT_LINK
        ),
      );
    }
  }, [can]);

  const remainingGuaranteedHours = 48;

  const locationName =
    thread.event_type === spaceName && venue.name ? venue.name : spaceName;

  return (
    <>
      <Box className='enquiry enquiry-details'>
        <Stack direction='column' spacing={2} sx={{ mb: 2 }}>
          <Stack sx={{ position: 'relative' }}>
            {isMobileDevice && (
              <div>
                <Link
                  to={history.location.pathname}
                  onClick={() => setActiveMobileView(inboxMobileView.CHAT)}>
                  <CtaNavBack />
                </Link>
              </div>
            )}
            <Box sx={{ maxWidth: '90%' }}>
              {thread.event_type && <p>{thread.event_type} at</p>}
              <Link target='_blank' to={spaceUrl}>
                <h1 className='h3'>{locationName}</h1>
              </Link>
            </Box>
            <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
              <div className='enquiry-details__buttons-container'>
                {!state.matches('cancelled') &&
                  !state.matches('booked') &&
                  !state.matches('expired') && (
                    <EditButton
                      isMobile={isMobileDevice}
                      thread={thread}
                      send={send}
                    />
                  )}
                {isMobileDevice && (
                  <CallButton
                    thread={thread}
                    venue={venue}
                    activeUser={activeUser}
                    isMobileDevice={isMobileDevice}
                  />
                )}
              </div>
            </Box>
            {state.matches('quoteReceived') &&
              hasQuoteBeenReceivedWithin48Hours(thread) && (
                <Box
                  sx={{ mt: 1 }}
                  className='enquiry-details__guaranteed-badge-container'>
                  <div className='guaranteed-badge'>
                    <p>Quote guaranteed for {remainingGuaranteedHours} hours</p>
                  </div>
                </Box>
              )}
          </Stack>
          <Box>
            <Link target='_blank' to={spaceUrl}>
              <div className='enquiry__header-photo'>
                <img alt='Venue' src={space.getPhoto()} />
              </div>
            </Link>
          </Box>
        </Stack>

        <OrganiserEnquiryInfo
          thread={thread}
          space={space}
          venue={venue}
          hasCtas={!isMobileDevice && hasCtas}
          state={state}
        />
      </Box>
      {!isMobileDevice && (
        <>
          {state.matches('open') && (
            <RequestQuoteCTAs thread={thread} can={can} send={send} />
          )}
          {state.matches('quoteRequested') && (
            <QuoteRequestedCTAs thread={thread} can={can} send={send} />
          )}
          {state.matches('quoteReceived') && (
            <QuoteReceivedCTAs thread={thread} can={can} send={send} />
          )}
          {state.matches('booked') && (
            <BookedCTAs thread={thread} send={send} />
          )}
          {state.matches('cancelled') && (
            <CancelledCTAs thread={thread} can={can} send={send} />
          )}
        </>
      )}

      {isMobileDevice && (
        <OrganiserMenu thread={thread} state={state} send={send} />
      )}
    </>
  );
};

function hasQuoteBeenReceivedWithin48Hours(thread) {
  const lastOfferTimestamp = thread.lastMessageWithOffer?.sendAt;

  return (
    lastOfferTimestamp &&
    dayjs().isSameOrBefore(dayjs(lastOfferTimestamp).add(48, 'hour'))
  );
}

function RequestQuoteCTAs({ thread, can, send }) {
  const canDecline = can.DECLINE || can.CANCEL || can.DECLINE_OR_CANCEL;

  return (
    <EnquiryCtas sx={{ px: 1.25, pb: 1 }}>
      {can.EDIT && (
        <Button
          fullWidth
          variant='contained'
          color='primary'
          size='medium-large'
          onClick={() => send({ type: 'REQUEST_QUOTE' })}>
          Request Quote
        </Button>
      )}

      {can.MARK_BOOKED_ORG && (
        <CtaMarkAsBooked
          type='button'
          thread={thread}
          onClick={() => {
            send({ type: 'MARK_AS_BOOKED' });
          }}
        />
      )}
      {canDecline && (
        <Button
          variant='text'
          color='primary'
          size='medium-large'
          onClick={() => send({ type: 'CANCEL' })}>
          Decline
        </Button>
      )}
      {thread.hide_from_organiser ? (
        <CtaShowToOrganiser thread={thread} />
      ) : null}
    </EnquiryCtas>
  );
}

function QuoteRequestedCTAs({ thread, can, send }) {
  const canDecline = can.DECLINE || can.CANCEL || can.DECLINE_OR_CANCEL;

  return (
    <EnquiryCtas sx={{ px: 1.25, pb: 1 }}>
      <CtaMarkAsBooked
        variant='contained'
        type='button'
        thread={thread}
        onClick={() => send({ type: 'MARK_AS_BOOKED' })}
      />
      {canDecline && (
        <Button
          fullWidth
          variant='outlined'
          size='medium-large'
          onClick={() => send({ type: 'CANCEL' })}>
          Decline
        </Button>
      )}
      <BookingRequestPending />
      {thread.hide_from_organiser ? (
        <CtaShowToOrganiser thread={thread} />
      ) : null}
    </EnquiryCtas>
  );
}

function QuoteReceivedCTAs({ thread, can, send }) {
  const canDecline = can.DECLINE || can.CANCEL || can.DECLINE_OR_CANCEL;

  return (
    <EnquiryCtas sx={{ px: 1.25, pb: 1 }}>
      <Button
        color='primary'
        variant='contained'
        size='medium-large'
        fullWidth
        onClick={() => send({ type: 'BOOK' })}>
        Book now
      </Button>
      {can.MARK_BOOKED_ORG && (
        <CtaMarkAsBooked
          type='button'
          thread={thread}
          onClick={() => send({ type: 'MARK_AS_BOOKED' })}
        />
      )}
      {canDecline && (
        <Button
          color='primary'
          variant='text'
          onClick={() => send({ type: 'CANCEL' })}>
          Decline
        </Button>
      )}

      {thread.hide_from_organiser ? (
        <CtaShowToOrganiser thread={thread} />
      ) : null}
    </EnquiryCtas>
  );
}

function BookedCTAs({ thread, send }) {
  const onClick = () => send({ type: 'UNMARK_AS_BOOKED' });

  const hasBookedWithOfflinePayment =
    thread.status === status.BOOKED && !thread.payment_required_to_book;

  if (thread.status === status.BOOKED_BY_ORG || hasBookedWithOfflinePayment) {
    return (
      <UnmarkedAsBooked
        copy='I have not booked this venue'
        onClick={onClick}
        thread={thread}
      />
    );
  }

  return (
    <EnquiryCtas sx={{ px: 1.25 }}>
      <Tip
        icon={<CheckCircleOutlineRounded sx={{ color: '#fff' }} />}
        title='Event booked'
        text='Congratulations! Your event has been booked'
      />
    </EnquiryCtas>
  );
}

function UnmarkedAsBooked({ copy, onClick }) {
  return (
    <EnquiryCtas sx={{ px: 1.25 }}>
      <Tip
        icon={<CheckCircleOutlineRounded sx={{ color: '#fff' }} />}
        title='Event booked'
        text={
          <>
            Made a mistake?&nbsp;
            <Button
              variant='text'
              sx={{
                color: '#fff',
                textDecoration: 'underline',
                p: 0,
                display: 'inline',
                fontSize: 14,
              }}
              onClick={onClick}>
              {copy}
            </Button>
          </>
        }
      />
    </EnquiryCtas>
  );
}

function CancelledCTAs({ can, send }) {
  if (can.REOPEN) {
    return (
      <EnquiryCtas sx={{ px: 1.25, pb: 2 }}>
        <Button
          fullWidth
          color='primary'
          variant='contained'
          size='medium-large'
          onClick={() => send({ type: 'REOPEN_ENQUIRY' })}>
          Re-open Enquiry
        </Button>
      </EnquiryCtas>
    );
  }

  return (
    <EnquiryCtas sx={{ px: 1.25, pb: 1 }}>
      <Tip
        sx={{ '& .MuiBox-root': { color: '#424242' } }}
        color='#eef0f4'
        icon={<CancelOutlined />}
        title='Event closed'
        text='If you still want to organise the event, please create a new enquiry on our platform.'
      />
    </EnquiryCtas>
  );
}

function BookingRequestPending() {
  return (
    <Tip
      icon={<QueryBuilderOutlined sx={{ color: '#fbb400' }} />}
      text="We've sent your booking request to the venue. They'll send you a quote if they're able to host you."
      title='Booking request pending'
    />
  );
}

OrganiserEnquiryDetails.propTypes = {
  can: PropTypes.object.isRequired,
  send: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
};

export default OrganiserEnquiryDetails;
